import React, { useEffect, useState } from "react";
import { Plus, Trash2, Loader2, X } from "lucide-react";
import { Button } from "../ui/button";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";

const EMPTY = {
  question: "",
  category: "",
  level: "quick",
  quick_answer: "",
  slug: "",
  sections: [],
  related_slugs: [],
  service: { label: "", url: "" },
  success_story: "",
  cta: { label: "", url: "" },
  review_frequency: "two_years",
  reviewed_at: "",
  featured: false,
  indexable: null,
};

const Field = ({ label, hint, children }) => (
  <label className="block mb-5">
    <span className="text-xs font-bold uppercase tracking-wide text-slate-500">{label}</span>
    {hint && <span className="block text-xs text-slate-400 mt-0.5 leading-relaxed">{hint}</span>}
    <div className="mt-2">{children}</div>
  </label>
);

const input = "w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm text-navy focus:outline-none focus:border-orange";

export const AskCassieEditor = ({ answer, preset, options, onClose, onSaved }) => {
  const { toast } = useToast();
  const [f, setF] = useState({ ...EMPTY, category: options.categories?.[0]?.key || "", ...(preset || {}) });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!answer) return;
    setF({ ...EMPTY, ...answer, service: answer.service || { label: "", url: "" }, cta: answer.cta || { label: "", url: "" } });
  }, [answer]);

  const set = (patch) => setF((v) => ({ ...v, ...patch }));
  const setSection = (i, patch) =>
    set({ sections: f.sections.map((s, k) => (k === i ? { ...s, ...patch } : s)) });

  const save = async () => {
    setBusy(true);
    try {
      const saved = answer
        ? await adminApi.askCassie.update(answer.id, f)
        : await adminApi.askCassie.create(f);
      toast({ title: answer ? "Answer saved" : "Answer created", description: "It stays a draft until you approve it." });
      onSaved(saved);
    } catch (e) {
      toast({ title: "Couldn't save", description: e?.response?.data?.detail || "Please check the fields.", variant: "destructive" });
    } finally {
      setBusy(false);
    }
  };

  const others = (options.answers || []).filter((a) => a.slug !== answer?.slug);

  return (
    <div className="fixed inset-0 z-50 bg-navy/50 backdrop-blur-sm overflow-y-auto p-4" data-testid="ask-cassie-editor">
      <div className="bg-white rounded-2xl w-full max-w-3xl mx-auto my-8 shadow-2xl">
        <div className="flex items-center justify-between p-5 border-b border-slate-100 sticky top-0 bg-white rounded-t-2xl">
          <h3 className="font-display font-bold text-navy">{answer ? "Edit answer" : "New answer"}</h3>
          <button onClick={onClose} data-testid="ask-cassie-editor-close" className="text-slate-400 hover:text-navy"><X size={20} /></button>
        </div>

        <div className="p-6">
          <Field label="The question" hint="Word it the way a business owner would ask it — that's what people search.">
            <input className={input} value={f.question} onChange={(e) => set({ question: e.target.value })} data-testid="editor-question" />
          </Field>

          <div className="grid sm:grid-cols-2 gap-x-5">
            <Field label="Category">
              <select className={input} value={f.category} onChange={(e) => set({ category: e.target.value })} data-testid="editor-category">
                {(options.categories || []).map((c) => <option key={c.key} value={c.key}>{c.name}</option>)}
                {options.holding && <option value={options.holding.key}>{options.holding.name} — park it for a decision</option>}
              </select>
            </Field>
            <Field label="Kind" hint="A full page only earns its place with a real example or trade-off to add.">
              <select className={input} value={f.level} onChange={(e) => set({ level: e.target.value })} data-testid="editor-level">
                <option value="quick">Quick answer — no page of its own</option>
                <option value="detailed">Full page at /ask-cassie/…</option>
              </select>
            </Field>
          </div>

          <Field label="Quick answer" hint="Two to four sentences. This is the first thing a reader — and an AI engine — sees.">
            <textarea rows={4} className={input} value={f.quick_answer} onChange={(e) => set({ quick_answer: e.target.value })} data-testid="editor-quick" />
          </Field>

          {f.level === "detailed" && (
            <>
              <Field label="Web address" hint="Leave blank to build it from the question. Changing it on a published answer breaks the old link.">
                <input className={input} placeholder="auto from the question" value={f.slug} onChange={(e) => set({ slug: e.target.value })} data-testid="editor-slug" />
              </Field>

              <div className="mb-5">
                <span className="text-xs font-bold uppercase tracking-wide text-slate-500">The detail</span>
                <span className="block text-xs text-slate-400 mt-0.5">Optional sections — use only the ones that earn their place. "In practice", "Who this applies to", "Common mistakes".</span>
                <div className="mt-3 space-y-3">
                  {f.sections.map((s, i) => (
                    <div key={i} className="rounded-xl border border-slate-200 p-4" data-testid={`editor-section-${i}`}>
                      <div className="flex items-center gap-2">
                        <input className={input} placeholder="Heading" value={s.heading || ""} onChange={(e) => setSection(i, { heading: e.target.value })} />
                        <button onClick={() => set({ sections: f.sections.filter((_, k) => k !== i) })} className="text-red-400 hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
                      </div>
                      <textarea rows={5} className={`${input} mt-2`} placeholder="Body — leave a blank line between paragraphs" value={s.body || ""} onChange={(e) => setSection(i, { body: e.target.value })} />
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => set({ sections: [...f.sections, { heading: "", body: "" }] })}
                  data-testid="editor-add-section"
                  className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-orange hover:text-orange-dark"
                >
                  <Plus size={15} /> Add a section
                </button>
              </div>

              <Field label="Related questions" hint="Chosen by hand, never by keyword. Up to five.">
                <select
                  multiple
                  size={6}
                  className={input}
                  value={f.related_slugs}
                  onChange={(e) => set({ related_slugs: Array.from(e.target.selectedOptions).map((o) => o.value).slice(0, 5) })}
                  data-testid="editor-related"
                >
                  {others.map((a) => <option key={a.slug} value={a.slug}>{a.question}</option>)}
                </select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-x-5">
                <Field label="The relevant service">
                  <select
                    className={input}
                    value={f.service.url}
                    onChange={(e) => {
                      const s = (options.services || []).find((x) => x.url === e.target.value);
                      set({ service: { url: e.target.value, label: s?.label || f.service.label } });
                    }}
                    data-testid="editor-service"
                  >
                    <option value="">None</option>
                    <option value="/services">All services</option>
                    <option value="/pricing">Pricing / package builder</option>
                    <option value="/legal-cashiering">Legal Cashiering</option>
                    {(options.services || []).map((s) => <option key={s.url} value={s.url}>{s.label}</option>)}
                  </select>
                  <input className={`${input} mt-2`} placeholder="Link wording" value={f.service.label} onChange={(e) => set({ service: { ...f.service, label: e.target.value } })} />
                </Field>
                <Field label="A client example">
                  <select className={input} value={f.success_story} onChange={(e) => set({ success_story: e.target.value })} data-testid="editor-story">
                    <option value="">None</option>
                    {(options.stories || []).map((s) => <option key={s.slug} value={s.slug}>{s.label}</option>)}
                  </select>
                </Field>
              </div>

              <Field label="One call to action" hint="One primary action per page. The Discovery Call is already in the navigation.">
                <div className="grid sm:grid-cols-2 gap-2">
                  <input className={input} placeholder="Wording, e.g. Talk it through with us" value={f.cta.label} onChange={(e) => set({ cta: { ...f.cta, label: e.target.value } })} data-testid="editor-cta-label" />
                  <input className={input} placeholder="/contact" value={f.cta.url} onChange={(e) => set({ cta: { ...f.cta, url: e.target.value } })} data-testid="editor-cta-url" />
                </div>
              </Field>
            </>
          )}

          <div className="grid sm:grid-cols-2 gap-x-5">
            <Field label="Review frequency" hint="Tax, VAT, payroll and solicitors' rules move; evergreen topics don't. A Working with CASS answer shows a date instead of a reviewer.">
              <select className={input} value={f.review_frequency} onChange={(e) => set({ review_frequency: e.target.value })} data-testid="editor-frequency">
                {(options.frequencies || []).map((x) => <option key={x.key} value={x.key}>{x.label}</option>)}
              </select>
            </Field>
            <Field label="Last reviewed" hint="Set by approving the answer. Only change it if the real review date differs.">
              <input type="date" className={input} value={f.reviewed_at || ""} onChange={(e) => set({ reviewed_at: e.target.value })} data-testid="editor-reviewed" />
            </Field>
          </div>

          <Field
            label="Google"
            hint="Subject answers are indexed. A Working with CASS answer is indexed only where there's real search demand — cost, switching, onboarding, getting started — and is otherwise reachable and linked but kept out of Google."
          >
            <select
              className={input}
              value={f.indexable === true ? "yes" : f.indexable === false ? "no" : ""}
              onChange={(e) => set({ indexable: e.target.value === "" ? null : e.target.value === "yes" })}
              data-testid="editor-indexable"
            >
              <option value="">Use the default for this category</option>
              <option value="yes">Let Google index it</option>
              <option value="no">Keep it out of Google</option>
            </select>
          </Field>

          <label className="flex items-center gap-2.5 text-sm text-slate-700 mb-6">
            <input type="checkbox" checked={!!f.featured} onChange={(e) => set({ featured: e.target.checked })} className="accent-orange w-4 h-4" data-testid="editor-featured" />
            Show in "Most asked" on the hub
          </label>

          <div className="flex gap-3">
            <Button onClick={save} disabled={busy} className="bg-orange hover:bg-orange-dark text-white rounded-full" data-testid="editor-save">
              {busy && <Loader2 size={15} className="mr-1.5 animate-spin" />} Save as draft
            </Button>
            <Button onClick={onClose} variant="outline" className="rounded-full">Cancel</Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AskCassieEditor;
